import { useCallback, useRef, useState } from 'react';
import SignaturePad from 'signature_pad';
import { exportarFirma } from './firmaImagen';

/**
 * Hook que envuelve signature_pad@5.
 *
 * Usa un **callback ref** en lugar de useRef porque el canvas vive dentro de un
 * Drawer con destroyOnHidden: el elemento se monta y desmonta cada vez que abre.
 * Un useEffect tradicional no se entera de eso, así que el SignaturePad nunca se
 * ataba al canvas vivo.
 *
 * @param {object} opts
 * @param {string} opts.backgroundColor — default 'rgba(0,0,0,0)' (transparente)
 * @param {string} opts.penColor — default '#000' (se exporta tal cual al PNG)
 * @returns {{
 *   canvasRef: (el: HTMLCanvasElement|null) => void,
 *   hasInk: boolean,
 *   clear: () => void,
 *   undo: () => void,
 *   isEmpty: () => boolean,
 *   toBlob: () => Promise<Blob>,
 * }}
 */
export function useSignaturePad({ backgroundColor = 'rgba(0,0,0,0)', penColor = '#000' } = {}) {
  const padRef = useRef(null);
  const canvasElRef = useRef(null);
  const observerRef = useRef(null);
  const orientationHandlerRef = useRef(null);
  // Reactivo: el botón "Guardar" depende de que haya tinta, no del ref.
  const [hasInk, setHasInk] = useState(false);

  const cleanup = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (orientationHandlerRef.current) {
      window.removeEventListener('orientationchange', orientationHandlerRef.current);
      orientationHandlerRef.current = null;
    }
    if (padRef.current) {
      padRef.current.off();
      padRef.current = null;
    }
    canvasElRef.current = null;
    setHasInk(false);
  }, []);

  // Callback ref: se ejecuta cuando React monta o desmonta el canvas.
  const canvasRef = useCallback(
    (canvas) => {
      if (!canvas) {
        cleanup();
        return;
      }
      // Si por alguna razón ya estaba montado el mismo canvas, no recrear
      if (canvasElRef.current === canvas) return;

      cleanup();
      canvasElRef.current = canvas;

      const pad = new SignaturePad(canvas, {
        backgroundColor,
        penColor,
        minWidth: 0.8,
        maxWidth: 2.5,
      });
      padRef.current = pad;
      pad.addEventListener('endStroke', () => setHasInk(!pad.isEmpty()));

      // Ajusta la resolución interna al tamaño CSS y al devicePixelRatio. Conserva
      // los trazos: signature_pad guarda puntos, no píxeles, así que se redibujan.
      const resize = () => {
        const w = canvas.offsetWidth;
        const h = canvas.offsetHeight;
        if (w === 0 || h === 0) return;
        // Mínimo 2×: en escritorio (DPR 1) la firma recortada quedaría de ~370 px de
        // ancho y se vería borrosa al imprimirse en el PDF.
        const ratio = Math.max(window.devicePixelRatio || 1, 2);
        const data = pad.toData();
        canvas.width = w * ratio;
        canvas.height = h * ratio;
        canvas.getContext('2d').scale(ratio, ratio);
        pad.clear();
        if (data && data.length > 0) {
          pad.fromData(data);
        }
      };

      const observer = new ResizeObserver(() => resize());
      observer.observe(canvas);
      observerRef.current = observer;

      const onOrientationChange = () => resize();
      window.addEventListener('orientationchange', onOrientationChange);
      orientationHandlerRef.current = onOrientationChange;
    },
    [backgroundColor, penColor, cleanup]
  );

  const clear = useCallback(() => {
    padRef.current?.clear();
    setHasInk(false);
  }, []);

  const undo = useCallback(() => {
    const pad = padRef.current;
    if (!pad) return;
    const data = pad.toData();
    if (!data || data.length === 0) return;
    data.pop();
    pad.fromData(data);
    setHasInk(data.length > 0);
  }, []);

  const isEmpty = useCallback(() => padRef.current?.isEmpty() ?? true, []);

  // PNG transparente recortado a la tinta (ver firmaImagen.js).
  const toBlob = useCallback(() => {
    const canvas = canvasElRef.current;
    if (!canvas) return Promise.reject(new Error('Canvas no montado'));
    return exportarFirma(canvas);
  }, []);

  return { canvasRef, hasInk, clear, undo, isEmpty, toBlob };
}
